/**
 * AvailabilityGrid — the tap-to-answer calendar in the intake form.
 *
 * Weeks of the trip horizon are laid out as columns. Tapping a week cycles it
 * preferred → possible → blackout → preferred. Tap-and-hold (~500 ms) opens that
 * week's seven day cells for finer control; that popover is the only day-level UI.
 *
 * The component is **self-persisting**: give it ids and a horizon and it loads,
 * debounces, and saves on its own. There is deliberately no value/onChange.
 *
 * Owned by spz-06-availability-grid. Embedded by spz-05 (intake page).
 */

import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import {
  DAYS_PER_WEEK,
  WEEK_START_DAY,
  addDays,
  createAvailabilityStore,
  dayOfWeek,
  nextLevel,
  startOfWeek,
  supabaseAvailabilityClient,
  toOrdinal,
  type AvailabilityClient,
  type AvailabilityLevel,
  type AvailabilityStore,
  type AvailabilityStoreStatus,
  type ISODate,
} from '../lib/availabilityStore'
import { isSupabaseConfigured, supabase } from '../lib/supabaseClient'

export interface AvailabilityGridProps {
  tripId: string
  /** ISO date, inclusive. */
  horizonStart: ISODate
  /** ISO date, inclusive. */
  horizonEnd: ISODate
  householdId: string
  /** Default false. Renders the same visuals with every handler off. */
  readOnly?: boolean
  /** Fires after each successful debounced save. */
  onSaved?: () => void
  /**
   * Persistence backend. Defaults to the shared browser Supabase client from
   * `src/lib/supabaseClient.ts`; injectable so spz-05 and tests can substitute one.
   */
  client?: AvailabilityClient
  /** Default 800 ms. Exposed for tests. */
  debounceMs?: number
}

// --- presentation ----------------------------------------------------------

/** Colorblind-safe palette (Okabe–Ito blue / vermillion), never hue alone. */
const LEVEL_STYLE: Record<AvailabilityLevel, { fill: string; ink: string; glyph: string; word: string }> = {
  preferred: { fill: '#0072B2', ink: '#ffffff', glyph: '✓', word: 'preferred' },
  possible: { fill: 'var(--surface-sunk, #ECEEEB)', ink: 'var(--muted, #5e6b68)', glyph: '·', word: 'possible' },
  blackout: { fill: '#D55E00', ink: '#ffffff', glyph: '✕', word: 'blackout' },
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

function monthOf(iso: ISODate): number {
  return Number(iso.slice(5, 7)) - 1
}

/** e.g. `Jul 11`. */
function shortDate(iso: ISODate): string {
  return `${MONTHS[monthOf(iso)]} ${Number(iso.slice(8, 10))}`
}

/** e.g. `Sat Jul 11`. */
function dayLabel(iso: ISODate): string {
  return `${WEEKDAYS[dayOfWeek(iso)]} ${shortDate(iso)}`
}

// --- week model ------------------------------------------------------------

type WeekLevel = AvailabilityLevel | 'mixed'

interface WeekModel {
  start: ISODate
  /** All 7 calendar days, flagged for whether they fall inside the horizon. */
  cells: { date: ISODate; inHorizon: boolean }[]
  /** Only the in-horizon days — the ones a tap actually writes. */
  activeDays: ISODate[]
  label: string
  /** Non-null on the first column of each month; marks the boundary. */
  monthLabel: string | null
}

/**
 * Columns from the {@link WEEK_START_DAY} on-or-before `horizonStart` through the
 * week containing `horizonEnd`. Days outside the horizon inside edge weeks are kept
 * in the model so the column still renders 7 slots, but they are disabled blanks.
 */
export function buildWeeks(horizonStart: ISODate, horizonEnd: ISODate): WeekModel[] {
  const weeks: WeekModel[] = []
  const lo = toOrdinal(horizonStart)
  const hi = toOrdinal(horizonEnd)
  if (hi < lo) return weeks

  let prevMonth = -1
  for (let start = startOfWeek(horizonStart); toOrdinal(start) <= hi; start = addDays(start, DAYS_PER_WEEK)) {
    const cells = Array.from({ length: DAYS_PER_WEEK }, (_, i) => {
      const date = addDays(start, i)
      const n = toOrdinal(date)
      return { date, inHorizon: n >= lo && n <= hi }
    })
    const activeDays = cells.filter((c) => c.inHorizon).map((c) => c.date)
    // Labelled by the week's own start day (a WEEK_START_DAY), not by the first
    // in-horizon day — otherwise the leading edge week lies about its boundary.
    const month = monthOf(start)
    weeks.push({
      start,
      cells,
      activeDays,
      label: shortDate(start),
      monthLabel: month !== prevMonth ? MONTHS[month]! : null,
    })
    prevMonth = month
  }
  return weeks
}

/** A week reads as its shared level, or `'mixed'` when its in-horizon days disagree. */
export function weekLevel(week: WeekModel, days: Map<ISODate, AvailabilityLevel>): WeekLevel {
  let seen: AvailabilityLevel | null = null
  for (const d of week.activeDays) {
    const level = days.get(d) ?? 'possible'
    if (seen === null) seen = level
    else if (seen !== level) return 'mixed'
  }
  return seen ?? 'possible'
}

/**
 * Tapping a uniform week advances every in-horizon day one step. Tapping a **mixed**
 * week restarts the cycle at `'preferred'` rather than advancing each day
 * independently — otherwise a mixed week can never be made uniform by tapping.
 * spz-05 inherits this behaviour; document it in the intake copy.
 */
export function weekTapLevel(current: WeekLevel): AvailabilityLevel {
  return current === 'mixed' ? 'preferred' : nextLevel(current)
}

// --- default client --------------------------------------------------------

/**
 * Used when the build has no Supabase env vars. A grid rendered without an
 * explicit `client` prop is then purely local: it keeps taps in memory rather
 * than pretending a save succeeded against a backend that isn't reachable.
 */
function createMemoryClient(): AvailabilityClient {
  let stored: Awaited<ReturnType<AvailabilityClient['select']>> = []
  return {
    async select() {
      return stored
    },
    async replace(tripId, householdId, rows) {
      stored = rows.map((r, i) => ({ ...r, id: `local-${tripId}-${householdId}-${i}` }))
    },
  }
}

// --- component -------------------------------------------------------------

const LONG_PRESS_MS = 500

/**
 * Named and default export are the same component. spz-05 imports it by name
 * (`import { AvailabilityGrid } from '../components/AvailabilityGrid'`); keep both.
 */
export function AvailabilityGrid({
  tripId,
  householdId,
  horizonStart,
  horizonEnd,
  readOnly = false,
  onSaved,
  client,
  debounceMs,
}: AvailabilityGridProps): JSX.Element {
  const weeks = useMemo(() => buildWeeks(horizonStart, horizonEnd), [horizonStart, horizonEnd])

  // Keep the latest onSaved without rebuilding the store (and losing pending edits)
  // every time the parent re-renders with a fresh closure.
  const onSavedRef = useRef(onSaved)
  onSavedRef.current = onSaved

  // Built from the horizon, so it is rebuilt when the horizon moves. `client` wins
  // when supplied; otherwise the shared browser client persists under the caller's
  // JWT and RLS decides what they may write.
  const defaultClient = useMemo(
    () =>
      isSupabaseConfigured
        ? supabaseAvailabilityClient(supabase, horizonStart, horizonEnd)
        : createMemoryClient(),
    [horizonStart, horizonEnd],
  )
  const activeClient = client ?? defaultClient

  const store: AvailabilityStore = useMemo(
    () =>
      createAvailabilityStore({
        client: activeClient,
        tripId,
        householdId,
        horizonStart,
        horizonEnd,
        debounceMs,
        onSaved: () => onSavedRef.current?.(),
      }),
    [activeClient, tripId, householdId, horizonStart, horizonEnd, debounceMs],
  )

  const [days, setDays] = useState<Map<ISODate, AvailabilityLevel>>(() => store.getDays())
  const [status, setStatus] = useState<AvailabilityStoreStatus>(() => store.getStatus())
  const [loadError, setLoadError] = useState<string | null>(null)
  const [openWeek, setOpenWeek] = useState<number | null>(null)

  useEffect(() => {
    let live = true
    const unsubscribe = store.subscribe((next) => {
      if (!live) return
      setDays(next)
      setStatus(store.getStatus())
    })
    store.load().catch((err: unknown) => {
      if (live) setLoadError(err instanceof Error ? err.message : String(err))
    })
    return () => {
      live = false
      unsubscribe()
      // Never drop a pending edit on unmount.
      void store.flush()
    }
  }, [store])

  const apply = useCallback(
    (dates: ISODate[], level: AvailabilityLevel) => {
      if (readOnly || dates.length === 0) return
      store.setLevel(dates, level)
    },
    [readOnly, store],
  )

  // --- long-press plumbing -------------------------------------------------
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longFired = useRef(false)

  const clearPress = useCallback(() => {
    if (pressTimer.current !== null) {
      clearTimeout(pressTimer.current)
      pressTimer.current = null
    }
  }, [])

  useEffect(() => clearPress, [clearPress])

  const onWeekPointerDown = useCallback(
    (index: number) => {
      if (readOnly) return
      longFired.current = false
      clearPress()
      pressTimer.current = setTimeout(() => {
        pressTimer.current = null
        longFired.current = true
        setOpenWeek(index)
      }, LONG_PRESS_MS)
    },
    [clearPress, readOnly],
  )

  const onWeekClick = useCallback(
    (week: WeekModel, current: WeekLevel) => {
      if (readOnly) return
      // Swallow the click the browser fires after a long press.
      if (longFired.current) {
        longFired.current = false
        return
      }
      apply(week.activeDays, weekTapLevel(current))
    },
    [apply, readOnly],
  )

  const open = openWeek === null ? null : (weeks[openWeek] ?? null)

  const statusText = loadError
    ? `Could not load: ${loadError}`
    : status.error
      ? 'Unsaved — will retry'
      : status.saving
        ? 'Saving…'
        : status.dirty
          ? 'Unsaved'
          : 'Saved'

  return (
    <div className="spz-ag" data-readonly={readOnly || undefined}>
      <style>{CSS}</style>

      <div className="spz-ag__scroll">
        <div className="spz-ag__track" role="group" aria-label="Availability by week">
          {weeks.map((week, i) => {
            const level = weekLevel(week, days)
            const style = level === 'mixed' ? null : LEVEL_STYLE[level]
            return (
              <div className="spz-ag__col" key={week.start} data-month={week.monthLabel ? '' : undefined}>
                <div className="spz-ag__month">{week.monthLabel ?? ' '}</div>
                <div className="spz-ag__label">{week.label}</div>
                <button
                  type="button"
                  className="spz-ag__cell"
                  aria-label={`Week of ${week.label}: ${level}`}
                  aria-disabled={readOnly || undefined}
                  disabled={readOnly}
                  style={style ? { background: style.fill, color: style.ink } : undefined}
                  onPointerDown={() => onWeekPointerDown(i)}
                  onPointerUp={clearPress}
                  onPointerCancel={clearPress}
                  onPointerLeave={clearPress}
                  onContextMenu={(e) => e.preventDefault()}
                  onClick={() => onWeekClick(week, level)}
                >
                  {style ? (
                    <span className="spz-ag__glyph" aria-hidden="true">
                      {style.glyph}
                    </span>
                  ) : (
                    <>
                      <span className="spz-ag__bars" aria-hidden="true">
                        {week.cells.map((c) => (
                          <i
                            key={c.date}
                            style={{
                              background: c.inHorizon
                                ? LEVEL_STYLE[days.get(c.date) ?? 'possible'].fill
                                : 'transparent',
                            }}
                          />
                        ))}
                      </span>
                      <span className="spz-ag__mixword" aria-hidden="true">
                        mix
                      </span>
                    </>
                  )}
                </button>
              </div>
            )
          })}
        </div>
      </div>

      <div className="spz-ag__footer">
        <ul className="spz-ag__legend">
          {(['preferred', 'possible', 'blackout'] as const).map((l) => (
            <li key={l}>
              <span
                className="spz-ag__swatch"
                style={{ background: LEVEL_STYLE[l].fill, color: LEVEL_STYLE[l].ink }}
                aria-hidden="true"
              >
                {LEVEL_STYLE[l].glyph}
              </span>
              {LEVEL_STYLE[l].word}
            </li>
          ))}
        </ul>
        <p className="spz-ag__status" role="status" data-dirty={status.dirty || undefined}>
          {statusText}
        </p>
      </div>

      {!readOnly && open !== null && (
        <div className="spz-ag__popover" role="dialog" aria-label={`Days in the week of ${open.label}`}>
          <div className="spz-ag__pophead">
            <strong>Week of {open.label}</strong>
            <button type="button" className="spz-ag__close" onClick={() => setOpenWeek(null)}>
              Done
            </button>
          </div>
          <div className="spz-ag__popdays">
            {open.cells.map((cell) => {
              const level = days.get(cell.date) ?? 'possible'
              const style = LEVEL_STYLE[level]
              return (
                <button
                  type="button"
                  key={cell.date}
                  className="spz-ag__day"
                  disabled={!cell.inHorizon}
                  aria-label={
                    cell.inHorizon
                      ? `${dayLabel(cell.date)}: ${level}`
                      : `${dayLabel(cell.date)}: outside the trip horizon`
                  }
                  aria-disabled={!cell.inHorizon || undefined}
                  style={cell.inHorizon ? { background: style.fill, color: style.ink } : undefined}
                  onClick={() => apply([cell.date], nextLevel(level))}
                >
                  <span className="spz-ag__dow" aria-hidden="true">
                    {WEEKDAYS[dayOfWeek(cell.date)]}
                  </span>
                  <span className="spz-ag__dom" aria-hidden="true">
                    {cell.inHorizon ? Number(cell.date.slice(8, 10)) : ''}
                  </span>
                  <span className="spz-ag__glyph spz-ag__glyph--sm" aria-hidden="true">
                    {cell.inHorizon ? style.glyph : ''}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default AvailabilityGrid

/**
 * The one place the week boundary is defined. Re-exported here so a consumer that
 * only imports the grid can label its columns consistently. See the constant's
 * docs in `availabilityStore.ts` for the Saturday-vs-Friday open question.
 */
export { WEEK_START_DAY }

const CSS = `
.spz-ag { position: relative; display: flex; flex-direction: column; gap: 0.5rem; }
.spz-ag__scroll { overflow-x: auto; overflow-y: hidden; -webkit-overflow-scrolling: touch; max-width: 100%; }
.spz-ag__track { display: flex; gap: 0.25rem; padding: 0.125rem; width: max-content; }
.spz-ag__col { display: flex; flex-direction: column; align-items: center; gap: 0.125rem; }
.spz-ag__col[data-month] { border-left: 2px solid var(--muted, #5e6b68); padding-left: 0.375rem; margin-left: 0.25rem; }
.spz-ag__month { font-size: 0.6875rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--muted, #5e6b68); }
.spz-ag__label { font-size: 0.75rem; color: var(--muted, #5e6b68); white-space: nowrap; }
.spz-ag__cell {
  min-width: 56px; min-height: 56px; width: 56px; height: 56px;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
  border: 1px solid rgba(0,0,0,0.18); border-radius: 10px;
  background: var(--surface-sunk, #ECEEEB); color: var(--muted, #5e6b68);
  font: inherit; cursor: pointer; touch-action: manipulation; -webkit-user-select: none; user-select: none;
}
.spz-ag[data-readonly] .spz-ag__cell { cursor: default; opacity: 0.85; }
.spz-ag__glyph { font-size: 1.375rem; line-height: 1; }
.spz-ag__glyph--sm { font-size: 0.875rem; }
.spz-ag__bars { display: flex; gap: 1px; }
.spz-ag__bars i { display: block; width: 5px; height: 16px; border-radius: 1px; }
.spz-ag__mixword { font-size: 0.625rem; text-transform: uppercase; letter-spacing: 0.06em; }
.spz-ag__footer { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem 1rem; }
.spz-ag__legend { display: flex; gap: 0.75rem; list-style: none; padding: 0; margin: 0; font-size: 0.75rem; color: var(--muted, #5e6b68); }
.spz-ag__legend li { display: flex; align-items: center; gap: 0.25rem; }
.spz-ag__swatch { display: inline-flex; align-items: center; justify-content: center; width: 1.125rem; height: 1.125rem; border-radius: 4px; font-size: 0.75rem; }
.spz-ag__status { margin-left: auto; font-size: 0.75rem; color: var(--muted, #5e6b68); }
.spz-ag__status[data-dirty] { color: #D55E00; }
.spz-ag__popover {
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 5;
  background: var(--ground, #f4f5f3); color: inherit;
  border: 1px solid rgba(0,0,0,0.2); border-radius: 12px; padding: 0.625rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
}
.spz-ag__pophead { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem; font-size: 0.875rem; }
.spz-ag__close { font: inherit; font-size: 0.875rem; padding: 0.25rem 0.75rem; min-height: 32px; border-radius: 8px; border: 1px solid rgba(0,0,0,0.2); background: transparent; color: inherit; cursor: pointer; }
.spz-ag__popdays { display: flex; gap: 0.25rem; overflow-x: auto; }
.spz-ag__day {
  min-width: 44px; min-height: 44px; flex: 0 0 auto;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  border: 1px solid rgba(0,0,0,0.18); border-radius: 8px;
  background: var(--surface-sunk, #ECEEEB); color: var(--muted, #5e6b68);
  font: inherit; cursor: pointer; touch-action: manipulation;
}
.spz-ag__day:disabled { opacity: 0.35; cursor: default; }
.spz-ag__dow { font-size: 0.5625rem; text-transform: uppercase; letter-spacing: 0.04em; }
.spz-ag__dom { font-size: 0.8125rem; font-weight: 600; }
`
